import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Sparkles,
  Wand2,
  Settings2,
  Download,
  Trash2,
  X,
  ChevronDown,
  ChevronUp,
  Maximize2,
  RefreshCw,
  Loader2,
  Image as ImageIcon,
  Shuffle,
  Copy,
  Check,
} from 'lucide-react';
import { useImageStore, GeneratedImage } from '../store/imageStore';
import { useThemeStore } from '../store/themeStore';
import { SIZE_PRESETS } from '../services/imageService';

const ImageGen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useThemeStore((s) => s.theme);
  const isDark = theme === 'dark';

  const params = useImageStore((s) => s.params);
  const setParams = useImageStore((s) => s.setParams);
  const resetParams = useImageStore((s) => s.resetParams);
  const isGenerating = useImageStore((s) => s.isGenerating);
  const generatedImages = useImageStore((s) => s.generatedImages);
  const error = useImageStore((s) => s.error);
  const generate = useImageStore((s) => s.generate);
  const removeImage = useImageStore((s) => s.removeImage);
  const clearImages = useImageStore((s) => s.clearImages);
  const clearError = useImageStore((s) => s.clearError);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<GeneratedImage | null>(null);
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleGenerate = useCallback(async () => {
    await generate();
  }, [generate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isGenerating) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleDownload = async (img: GeneratedImage) => {
    try {
      const response = await fetch(img.imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vngpt-image-${img.id}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(img.imageUrl, '_blank');
    }
  };

  const handleCopyPrompt = async (prompt: string) => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedPrompt(prompt);
      setTimeout(() => setCopiedPrompt(null), 2000);
    } catch {}
  };

  const handleRandomSeed = () => {
    setParams({ seed: Math.floor(Math.random() * 999999999) });
  };

  const EXAMPLE_PROMPTS = [
    'A futuristic cyberpunk city at sunset, neon lights reflecting on wet streets',
    'A magical forest with glowing mushrooms and floating fireflies, fantasy art',
    'An astronaut riding a horse on Mars, digital painting, cinematic lighting',
    'A cute robot watering flowers in a garden, Studio Ghibli style',
    'Ancient Vietnamese temple in the mountains, misty morning, watercolor style',
    'A dragon made of galaxies and stars flying through space, cosmic art',
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className={`h-full flex flex-col ${isDark ? 'bg-gpt-dark-main' : 'bg-gpt-light-main'}`}
    >
      {/* ─── Header ─── */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={`flex items-center justify-between px-4 md:px-6 h-[60px] flex-shrink-0 border-b ${
          isDark ? 'border-gpt-dark-border/30' : 'border-gpt-light-border'
        }`}
      >
        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.05, x: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className={`p-2 rounded-lg transition-colors ${
              isDark
                ? 'hover:bg-gpt-dark-hover text-gpt-dark-text-secondary'
                : 'hover:bg-gpt-light-hover text-gpt-light-text-secondary'
            }`}
          >
            <ArrowLeft size={20} />
          </motion.button>

          <div className="flex items-center gap-2">
            <motion.div
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <Wand2
                size={22}
                className="text-violet-400"
              />
            </motion.div>
            <h1
              className={`text-lg font-semibold ${
                isDark ? 'text-gpt-dark-text' : 'text-gpt-light-text'
              }`}
            >
              Image Studio
            </h1>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 font-medium">
              BETA
            </span>
          </div>
        </div>

        {generatedImages.length > 0 && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={clearImages}
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
              isDark
                ? 'text-gpt-dark-text-secondary hover:bg-gpt-dark-hover'
                : 'text-gpt-light-text-secondary hover:bg-gpt-light-hover'
            }`}
          >
            Clear all
          </motion.button>
        )}
      </motion.header>

      {/* ─── Main Content ─── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
          {/* ─── Prompt Input Card ─── */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className={`rounded-2xl border overflow-hidden ${
              isDark
                ? 'bg-gpt-dark-input border-gpt-dark-border/50'
                : 'bg-gpt-light-input border-gpt-light-border'
            }`}
          >
            {/* Prompt textarea */}
            <div className="p-4">
              <label
                className={`text-xs font-medium mb-2 block ${
                  isDark ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
                }`}
              >
                Describe your image
              </label>
              <textarea
                ref={textareaRef}
                value={params.prompt}
                onChange={(e) => {
                  setParams({ prompt: e.target.value });
                  clearError();
                }}
                onKeyDown={handleKeyDown}
                placeholder="A breathtaking landscape with mountains and aurora borealis..."
                rows={3}
                className={`w-full resize-none bg-transparent text-[15px] leading-relaxed outline-none placeholder:transition-colors ${
                  isDark
                    ? 'text-gpt-dark-text placeholder:text-gpt-dark-text-secondary/50'
                    : 'text-gpt-light-text placeholder:text-gpt-light-text-secondary/50'
                }`}
              />
            </div>

            {/* Example prompts */}
            <div className="px-4 pb-3">
              <div className="flex flex-wrap gap-1.5">
                {EXAMPLE_PROMPTS.slice(0, 3).map((prompt, i) => (
                  <motion.button
                    key={i}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setParams({ prompt })}
                    className={`text-[11px] px-2.5 py-1 rounded-full transition-colors truncate max-w-[200px] ${
                      isDark
                        ? 'bg-gpt-dark-hover/50 text-gpt-dark-text-secondary hover:bg-gpt-dark-hover hover:text-gpt-dark-text'
                        : 'bg-gpt-light-hover/50 text-gpt-light-text-secondary hover:bg-gpt-light-hover hover:text-gpt-light-text'
                    }`}
                  >
                    {prompt.slice(0, 40)}...
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Size presets */}
            <div
              className={`px-4 py-3 border-t flex items-center justify-between gap-3 ${
                isDark ? 'border-gpt-dark-border/30' : 'border-gpt-light-border'
              }`}
            >
              <div className="flex items-center gap-1.5 overflow-x-auto">
                {SIZE_PRESETS.map((preset) => (
                  <motion.button
                    key={preset.label}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() =>
                      setParams({ width: preset.width, height: preset.height })
                    }
                    className={`text-[11px] px-2.5 py-1.5 rounded-lg whitespace-nowrap transition-all ${
                      params.width === preset.width && params.height === preset.height
                        ? 'bg-violet-500/20 text-violet-400 font-medium ring-1 ring-violet-500/30'
                        : isDark
                        ? 'text-gpt-dark-text-secondary hover:bg-gpt-dark-hover'
                        : 'text-gpt-light-text-secondary hover:bg-gpt-light-hover'
                    }`}
                  >
                    {preset.icon} {preset.label}
                  </motion.button>
                ))}
              </div>

              {/* Advanced toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAdvanced(!showAdvanced)}
                className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg flex-shrink-0 transition-colors ${
                  isDark
                    ? 'text-gpt-dark-text-secondary hover:bg-gpt-dark-hover'
                    : 'text-gpt-light-text-secondary hover:bg-gpt-light-hover'
                }`}
              >
                <Settings2 size={13} />
                <span className="hidden sm:inline">Advanced</span>
                {showAdvanced ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </motion.button>
            </div>

            {/* Advanced settings */}
            <AnimatePresence>
              {showAdvanced && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div
                    className={`px-4 py-4 border-t grid grid-cols-1 sm:grid-cols-3 gap-4 ${
                      isDark ? 'border-gpt-dark-border/30' : 'border-gpt-light-border'
                    }`}
                  >
                    {/* Steps */}
                    <div>
                      <label
                        className={`text-xs font-medium mb-1.5 block ${
                          isDark ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
                        }`}
                      >
                        Steps: {params.steps}
                      </label>
                      <input
                        type="range"
                        min={1}
                        max={50}
                        value={params.steps}
                        onChange={(e) => setParams({ steps: parseInt(e.target.value) })}
                        className="w-full accent-violet-500 h-1.5"
                      />
                      <div
                        className={`flex justify-between text-[10px] mt-1 ${
                          isDark ? 'text-gpt-dark-text-secondary/50' : 'text-gpt-light-text-secondary/50'
                        }`}
                      >
                        <span>Fast</span>
                        <span>Quality</span>
                      </div>
                    </div>

                    {/* Guidance */}
                    <div>
                      <label
                        className={`text-xs font-medium mb-1.5 block ${
                          isDark ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
                        }`}
                      >
                        Guidance: {params.guidance.toFixed(1)}
                      </label>
                      <input
                        type="range"
                        min={1}
                        max={20}
                        step={0.5}
                        value={params.guidance}
                        onChange={(e) => setParams({ guidance: parseFloat(e.target.value) })}
                        className="w-full accent-violet-500 h-1.5"
                      />
                      <div
                        className={`flex justify-between text-[10px] mt-1 ${
                          isDark ? 'text-gpt-dark-text-secondary/50' : 'text-gpt-light-text-secondary/50'
                        }`}
                      >
                        <span>Creative</span>
                        <span>Precise</span>
                      </div>
                    </div>

                    {/* Seed */}
                    <div>
                      <label
                        className={`text-xs font-medium mb-1.5 block ${
                          isDark ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
                        }`}
                      >
                        Seed
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={params.seed}
                          onChange={(e) => setParams({ seed: parseInt(e.target.value) || -1 })}
                          className={`flex-1 px-3 py-1.5 rounded-lg text-sm outline-none border transition-colors ${
                            isDark
                              ? 'bg-gpt-dark-main border-gpt-dark-border text-gpt-dark-text'
                              : 'bg-white border-gpt-light-border text-gpt-light-text'
                          }`}
                          placeholder="-1 for random"
                        />
                        <motion.button
                          whileHover={{ scale: 1.1, rotate: 180 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={handleRandomSeed}
                          className={`p-1.5 rounded-lg ${
                            isDark ? 'hover:bg-gpt-dark-hover text-gpt-dark-text-secondary' : 'hover:bg-gpt-light-hover text-gpt-light-text-secondary'
                          }`}
                          title="Random seed"
                        >
                          <Shuffle size={14} />
                        </motion.button>
                      </div>
                      <p
                        className={`text-[10px] mt-1 ${
                          isDark ? 'text-gpt-dark-text-secondary/50' : 'text-gpt-light-text-secondary/50'
                        }`}
                      >
                        -1 = random each time
                      </p>
                    </div>
                  </div>

                  {/* Reset */}
                  <div
                    className={`px-4 pb-3 flex justify-end`}
                  >
                    <button
                      onClick={resetParams}
                      className={`text-[11px] px-2.5 py-1 rounded-lg transition-colors ${
                        isDark
                          ? 'text-gpt-dark-text-secondary hover:bg-gpt-dark-hover'
                          : 'text-gpt-light-text-secondary hover:bg-gpt-light-hover'
                      }`}
                    >
                      Reset to defaults
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className={`px-4 py-2 border-t text-sm text-red-400 ${
                    isDark ? 'border-gpt-dark-border/30 bg-red-500/5' : 'border-gpt-light-border bg-red-50'
                  }`}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Generate button */}
            <div
              className={`p-3 border-t ${
                isDark ? 'border-gpt-dark-border/30' : 'border-gpt-light-border'
              }`}
            >
              <motion.button
                whileHover={!isGenerating ? { scale: 1.01 } : {}}
                whileTap={!isGenerating ? { scale: 0.99 } : {}}
                onClick={handleGenerate}
                disabled={isGenerating || !params.prompt.trim()}
                className={`w-full py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2.5 transition-all ${
                  isGenerating || !params.prompt.trim()
                    ? 'bg-violet-500/30 text-violet-300/50 cursor-not-allowed'
                    : 'bg-violet-600 hover:bg-violet-500 text-white shadow-lg shadow-violet-500/20 hover:shadow-violet-500/30'
                }`}
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Creating your masterpiece...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    <span>Generate Image</span>
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>

          {/* ─── Loading Animation ─── */}
          <AnimatePresence>
            {isGenerating && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-col items-center py-12"
              >
                <div className="relative w-24 h-24 mb-4">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 rounded-full border-2 border-transparent border-t-violet-500 border-r-violet-500/50"
                  />
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-2 rounded-full border-2 border-transparent border-b-purple-400 border-l-purple-400/50"
                  />
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Wand2 size={24} className="text-violet-400" />
                  </motion.div>
                </div>

                <motion.p
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className={`text-sm ${isDark ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'}`}
                >
                  AI is painting your imagination...
                </motion.p>

                <div className="flex gap-1 mt-3">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <motion.div
                      key={i}
                      animate={{ scale: [1, 1.5, 1], opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.15 }}
                      className="w-1.5 h-1.5 rounded-full bg-violet-500"
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ─── Generated Images Gallery ─── */}
          {generatedImages.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <h2
                className={`text-sm font-semibold mb-3 ${
                  isDark ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
                }`}
              >
                Generated Images ({generatedImages.length})
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <AnimatePresence mode="popLayout">
                  {generatedImages.map((img, index) => (
                    <motion.div
                      key={img.id}
                      layout
                      initial={{ opacity: 0, scale: 0.8, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{
                        type: 'spring',
                        damping: 20,
                        stiffness: 250,
                        delay: index === 0 ? 0.1 : 0,
                      }}
                      className={`group rounded-2xl overflow-hidden border transition-all ${
                        isDark
                          ? 'border-gpt-dark-border/50 bg-gpt-dark-input hover:border-violet-500/30'
                          : 'border-gpt-light-border bg-gpt-light-input hover:border-violet-500/30'
                      }`}
                    >
                      {/* Image */}
                      <div
                        className="relative aspect-square overflow-hidden cursor-pointer"
                        onClick={() => setLightboxImage(img)}
                      >
                        <img
                          src={img.imageUrl}
                          alt={img.prompt}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />

                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setLightboxImage(img);
                              }}
                              className="p-2 rounded-lg bg-white/10 backdrop-blur-sm text-white hover:bg-white/20"
                            >
                              <Maximize2 size={16} />
                            </motion.button>

                            <div className="flex items-center gap-1.5">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(img);
                                }}
                                className="p-2 rounded-lg bg-white/10 backdrop-blur-sm text-white hover:bg-white/20"
                              >
                                <Download size={16} />
                              </motion.button>

                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage(img.id);
                                }}
                                className="p-2 rounded-lg bg-red-500/20 backdrop-blur-sm text-red-300 hover:bg-red-500/30"
                              >
                                <Trash2 size={16} />
                              </motion.button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={`text-xs line-clamp-2 flex-1 ${
                              isDark ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
                            }`}
                          >
                            {img.prompt}
                          </p>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleCopyPrompt(img.prompt)}
                            className={`p-1 rounded flex-shrink-0 ${
                              isDark ? 'hover:bg-gpt-dark-hover text-gpt-dark-text-secondary' : 'hover:bg-gpt-light-hover text-gpt-light-text-secondary'
                            }`}
                            title="Copy prompt"
                          >
                            {copiedPrompt === img.prompt ? (
                              <Check size={12} className="text-green-400" />
                            ) : (
                              <Copy size={12} />
                            )}
                          </motion.button>
                        </div>

                        <div className="flex items-center gap-2 mt-2">
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                              isDark ? 'bg-gpt-dark-hover text-gpt-dark-text-secondary' : 'bg-gpt-light-hover text-gpt-light-text-secondary'
                            }`}
                          >
                            {img.params.width}×{img.params.height}
                          </span>
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                              isDark ? 'bg-gpt-dark-hover text-gpt-dark-text-secondary' : 'bg-gpt-light-hover text-gpt-light-text-secondary'
                            }`}
                          >
                            {img.params.steps} steps
                          </span>

                          {/* Reuse prompt button */}
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setParams(img.params);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="ml-auto flex items-center gap-1 text-[10px] text-violet-400 hover:text-violet-300 transition-colors"
                          >
                            <RefreshCw size={10} />
                            Reuse
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* ─── Empty State ─── */}
          {generatedImages.length === 0 && !isGenerating && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col items-center py-16"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-4 ${
                  isDark ? 'bg-violet-500/10' : 'bg-violet-50'
                }`}
              >
                <ImageIcon size={32} className="text-violet-400" />
              </motion.div>

              <p
                className={`text-base font-medium mb-1 ${
                  isDark ? 'text-gpt-dark-text' : 'text-gpt-light-text'
                }`}
              >
                Create stunning AI images
              </p>
              <p
                className={`text-sm ${
                  isDark ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
                }`}
              >
                Describe what you imagine and let AI bring it to life
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* ─── Lightbox ─── */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
            onClick={() => setLightboxImage(null)}
          >
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
              onClick={() => setLightboxImage(null)}
            >
              <X size={20} />
            </motion.button>

            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative max-w-[90vw] max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightboxImage.imageUrl}
                alt={lightboxImage.prompt}
                className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
              />

              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent rounded-b-xl">
                <p className="text-white text-sm mb-2 line-clamp-2">
                  {lightboxImage.prompt}
                </p>
                <div className="flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDownload(lightboxImage)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-white text-xs hover:bg-white/20 backdrop-blur-sm"
                  >
                    <Download size={14} />
                    Download
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleCopyPrompt(lightboxImage.prompt)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-white text-xs hover:bg-white/20 backdrop-blur-sm"
                  >
                    {copiedPrompt === lightboxImage.prompt ? (
                      <><Check size={14} /> Copied</>
                    ) : (
                      <><Copy size={14} /> Copy prompt</>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ImageGen;